import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import ChartList from "./chat/chartlist";
import PersonalChat from "./chat/personalchart";
import { practiceShortName } from "../config/practice";

/*
 * OSA's Messaging page (/messaging; features.messagingPage, 7825), ported from
 * its own build (fax/messaging.jsx): a full-screen conversation list with the
 * provider dropdown in its header and the open conversation beside it.
 *
 * The provider dropdown filters on a MESSAGE TAG: each conversation's `tags`
 * is the latest tagged chat_details.msg_tags (v1 messaging/chatmaster), and a
 * conversation matches when that text contains the chosen tag (case-
 * insensitive), exactly as OSA. OSA hard-coded its four tags ("dr suresh",
 * ...); here the options are the provider tags the practice actually has
 * (v1 messaging/tags plus the conversations' own tags, those starting with
 * profile.messaging.providerTagPrefix -- callNotes.providerTagOptions).
 *
 * Every send is a call note (callNotes.postCallNote via PersonalChat); no SMS.
 */

const closeIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const StethoscopeLogo = ({ className = "w-3 h-3" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M6 3v4.5a4.5 4.5 0 0 0 9 0V3" />
    <circle cx="6" cy="3" r="1" fill="currentColor" />
    <circle cx="15" cy="3" r="1" fill="currentColor" />
    <path d="M10.5 11.5v3a4.5 4.5 0 0 0 9 0v-1" />
    <circle cx="19.5" cy="13.5" r="2.5" fill="currentColor" />
  </svg>
);

// "dr suresh" -> "Dr Suresh" (OSA's labels for its tags).
export function providerLabel(tag) {
  return String(tag || "")
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

// The dropdown's rows: "All Providers" first, then one per provider tag.
export function providerChoices(tags) {
  return [{ id: "", label: "All Providers" }, ...(tags || []).map((t) => ({ id: t, label: providerLabel(t) }))];
}

export function ProviderDropdown({ options, selectedProvider, setSelectedProvider }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const providers = providerChoices(options);
  const currentProvider = providers.find((p) => p.id === selectedProvider) || providers[0];

  return (
    <div ref={dropdownRef} className="relative z-30">
      <button
        type="button"
        aria-label="Filter by provider"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white hover:bg-[#F9FAFB] border border-[#E5E7EB] hover:border-[#CBD5E1] text-[#1D2951] text-[11.5px] font-semibold transition-all shadow-xs group focus:outline-none focus:ring-2 focus:ring-[#E17954]/20"
      >
        <span
          className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 shadow-2xs ${
            currentProvider.id
              ? "bg-gradient-to-tr from-[#1D2951] to-[#2B3B70] text-white"
              : "bg-gradient-to-tr from-[#E17954] to-[#F7A072] text-white"
          }`}
        >
          <StethoscopeLogo className="w-3 h-3" />
        </span>
        <span className="truncate max-w-[95px]">{currentProvider.label}</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          className={`text-[#6B7280] group-hover:text-[#1D2951] transition-transform duration-200 flex-shrink-0 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
        >
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="absolute right-0 top-[calc(100%+8px)] w-[200px] bg-white border border-[#E5E7EB] rounded-2xl shadow-2xl z-50 py-1.5 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
        >
          <div className="px-3 py-1.5 text-[9.5px] font-bold uppercase tracking-wider text-[#9CA3AF] border-b border-[#F3F4F6] mb-1">
            Filter by Provider
          </div>
          {providers.map((provider) => {
            const isSelected = selectedProvider === provider.id;
            return (
              <button
                key={provider.id || "all"}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  setSelectedProvider(provider.id);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-left text-[11.5px] font-medium transition-colors ${
                  isSelected
                    ? "bg-[#FFF0E6] text-[#E17954] font-semibold"
                    : "text-[#374151] hover:bg-[#F9FAFB] hover:text-[#1D2951]"
                }`}
              >
                <div className="flex items-center gap-2 truncate">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isSelected
                        ? "bg-[#E17954] text-white"
                        : provider.id
                        ? "bg-[#1D2951]/10 text-[#1D2951]"
                        : "bg-[#E17954]/10 text-[#E17954]"
                    }`}
                  >
                    <StethoscopeLogo className="w-3 h-3" />
                  </div>
                  <span className="truncate">{provider.label}</span>
                </div>
                {isSelected && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#E17954"
                    strokeWidth="3"
                    className="flex-shrink-0"
                  >
                    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// OSA's right panel with no conversation open: "No Patient Selected" and the
// unread count.
function NoPatientSelected({ unreadCount }) {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full min-h-0 bg-[#F9FAFB] p-6 text-center select-none">
      <div className="max-w-[300px] w-full flex flex-col items-center">
        <div className="w-12 h-12 rounded-xl bg-[#1D2951]/5 text-[#1D2951] flex items-center justify-center mb-3">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>
        {unreadCount > 0 && (
          <div
            data-testid="osa-unread-count"
            className="mb-2.5 inline-flex items-center gap-1.5 px-3 py-1 bg-[#FFF0E6] text-[#E17954] text-[11px] font-semibold rounded-full border border-[#F7A072]/40 shadow-2xs animate-pulse"
          >
            <span className="relative flex h-2 w-2 items-center justify-center">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E17954] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#E17954]"></span>
            </span>
            {unreadCount} Unread Message{unreadCount > 1 ? "s" : ""}
          </div>
        )}
        <h3 className="text-[#1D2951] text-[15px] font-semibold">No Patient Selected</h3>
      </div>
    </div>
  );
}

export default function OsaMessaging({ onClose }) {
  const [chatPatientId, setChatPatientId] = useState("");
  const [, setChatToggleScreen] = useState(true);
  const [chatSelectedDate, setChatSelectedDate] = useState(dayjs());
  const [isNewPatient, setIsNewPatient] = useState(false);
  const [otherViews, setOtherViews] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("");
  const [providerOptions, setProviderOptions] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = `${practiceShortName()} - Messaging`;
  }, []);

  const hasPatient = Boolean(chatPatientId && chatPatientId.patientid);

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col bg-[#F5F7FA]">
      <div className="flex flex-1 overflow-hidden">
        {/* Left: conversation list */}
        <div className="w-[30%] min-w-[340px] max-w-[420px] flex flex-col bg-white border-r border-[#E5E7EB]">
          <div className="flex items-center justify-between px-3 h-[62px] bg-white border-b border-[#F1F3F9]">
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-8 h-8 rounded-xl bg-[#E17954] flex items-center justify-center shadow-xs flex-shrink-0">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <h1 className="text-[#1D2951] text-[15px] font-bold leading-tight">Messaging</h1>
            </div>
            <div className="flex items-center gap-2">
              <ProviderDropdown
                options={providerOptions}
                selectedProvider={selectedProvider}
                setSelectedProvider={setSelectedProvider}
              />
              <button
                type="button"
                onClick={() => (onClose ? onClose() : navigate("/landing"))}
                className="w-8 h-8 rounded-full bg-[#F3F4F6] hover:bg-[#E5E7EB] flex items-center justify-center text-[#6B7280] hover:text-[#1D2951] transition-colors focus:outline-none flex-shrink-0"
                title="Close Messaging"
              >
                {closeIcon}
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 h-full overflow-y-auto w-full" style={{ scrollbarWidth: "none" }}>
            <ChartList
              isNewPatient={isNewPatient}
              setIsNewPatient={setIsNewPatient}
              selectedDate={chatSelectedDate}
              setOtherViews={setOtherViews}
              otherviews={otherViews}
              setSelectedDate={setChatSelectedDate}
              setPatientId={setChatPatientId}
              patient={chatPatientId}
              setToggleScreen={setChatToggleScreen}
              selectedProvider={selectedProvider}
              setSelectedProvider={setSelectedProvider}
              onProviderOptions={setProviderOptions}
              setUnreadCount={setUnreadCount}
            />
          </div>
        </div>

        {/* Right: the open conversation */}
        <div className="flex-1 flex flex-col bg-white overflow-hidden">
          {hasPatient ? (
            <PersonalChat
              isNewPatient={isNewPatient}
              top="inline"
              setchat={() => {}}
              patient={chatPatientId}
              setToggleScreen={setChatToggleScreen}
            />
          ) : (
            <NoPatientSelected unreadCount={unreadCount} />
          )}
        </div>
      </div>
    </div>
  );
}
